/**
 * Invitation Errors Component
 *
 * Displays failed invitation (bounce) data from the API. Fetches from GET /api/bounces
 * and shows send date/time (受信日時) in the table.
 */
import React, { useState, useEffect, useCallback } from 'react';
import ErrorTable from './ErrorTable';
import { FailedInvitation } from './types';
import { API_BASE_URL } from '../../../../config';
import { formatBackendLocalDateTime } from '../../../../utils/japanTime';
import './InvitationErrors.css';
import backButtonIcon from '../../../../assests/icons/back-button.png';
import rotateIcon from '../../../../assests/icons/refresh.png';

/** API bounce row from GET /api/bounces (includes recipientName and readAt). */
interface BounceRow {
  id: number;
  originalRecipient: string;
  recipientName: string | null;
  errorMessage: string | null;
  graphMessageId: string | null;
  receivedAt: string | null;
  createdAt: string | null;
  readAt: string | null;
}

/** Strip surrounding double quotes and trim (e.g. "user@example.com" -> user@example.com). */
function normalizeEmail(s: string | null): string {
  if (s == null || s === '') return '—';
  const t = s.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1).trim();
  }
  return t || '—';
}

/** Strip HTML tags and decode common entities for plain-text display. */
function plainTextError(msg: string | null): string {
  if (msg == null || msg === '') return '—';
  let s = msg
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  s = s
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  return s.length > 0 ? s : '—';
}

function mapBouncesToFailed(bounces: BounceRow[]): FailedInvitation[] {
  return bounces.map((b) => ({
    id: String(b.id),
    name: (b.recipientName && b.recipientName.trim() !== '') ? b.recipientName.trim() : '—',
    email: normalizeEmail(b.originalRecipient),
    errorContent: plainTextError(b.errorMessage),
    sendTime: formatBackendLocalDateTime(b.receivedAt),
    isUnread: b.readAt == null || b.readAt === '',
  }));
}

export interface InvitationErrorsProps {
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Callback when retry button is clicked (sync bounces from mailbox) */
  onRetry?: () => void;
}

const InvitationErrors: React.FC<InvitationErrorsProps> = ({
  onBack,
  onRetry,
}) => {
  const [failedInvitations, setFailedInvitations] = useState<FailedInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadBounces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/bounces`);
      if (res.ok) {
        const data: BounceRow[] = await res.json();
        setFailedInvitations(mapBouncesToFailed(data));
      } else {
        setFailedInvitations([]);
      }
    } catch {
      setFailedInvitations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // When opening this page, sync from mailbox first; then re-sync once after 60s so
  // late-arriving bounces (often 1–2 min after send) appear without restarting the backend
  useEffect(() => {
    let cancelled = false;
    const syncThenLoad = async () => {
      try {
        await fetch(`${API_BASE_URL}/api/bounces/sync`, { method: 'POST' });
        if (!cancelled) await loadBounces();
      } catch {
        if (!cancelled) await loadBounces();
      }
    };
    syncThenLoad();
    const t = window.setTimeout(() => {
      if (cancelled) return;
      syncThenLoad();
    }, 60000);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [loadBounces]);

  const [detailItem, setDetailItem] = useState<FailedInvitation | null>(null);

  const handleRowClick = async (item: FailedInvitation) => {
    setDetailItem(item);
    try {
      await fetch(`${API_BASE_URL}/api/bounces/${item.id}/read`, { method: 'PUT' });
      await loadBounces();
    } catch {
      // still show popup
    }
  };

  const handleRetry = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/bounces/sync`, { method: 'POST' });
      if (res.ok) {
        await loadBounces();
      }
      if (onRetry) onRetry();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="invitation-errors">
      {/* Header Section */}
      <div className="invitation-errors__header">
        <button 
          className="invitation-errors__back-btn"
          onClick={onBack}
          aria-label="戻る"
        >
          <img src={backButtonIcon} alt="" className="invitation-errors__back-icon" aria-hidden />
        </button>
        <h1 className="invitation-errors__title">招待送信エラー</h1>
        <button 
          className="invitation-errors__retry-btn"
          onClick={handleRetry}
          disabled={syncing}
        >
          <img src={rotateIcon} alt="" className="invitation-errors__retry-icon" aria-hidden />
          <span>{syncing ? '同期中...' : 'メールボックスから取得'}</span>
        </button>
      </div>

      {/* Hint: bounces can be delayed */}
      <p className="invitation-errors__hint">
        バウンスメールは送信から1～2分程度遅れて届く場合があります。表示されない場合は「メールボックスから取得」を再度お試しください。
      </p>

      {/* Error Table Section */}
      <div className="invitation-errors__table-section">
        {loading ? (
          <p className="invitation-errors__loading">読み込み中...</p>
        ) : (
          <ErrorTable
            failedInvitations={failedInvitations}
            onRowClick={handleRowClick}
          />
        )}
      </div>

      {/* Detail popup: full error and details; closing it keeps the row marked as read */}
      {detailItem && (
        <div
          className="invitation-errors__popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invitation-errors-popup-title"
          onClick={() => setDetailItem(null)}
        >
          <div
            className="invitation-errors__popup"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="invitation-errors-popup-title" className="invitation-errors__popup-title">
              エラー詳細
            </h2>
            <dl className="invitation-errors__popup-dl">
              <dt>名前</dt>
              <dd>{detailItem.name}</dd>
              <dt>メールアドレス</dt>
              <dd>{detailItem.email}</dd>
              <dt>送信日時</dt>
              <dd>{detailItem.sendTime}</dd>
              <dt>エラー内容</dt>
              <dd className="invitation-errors__popup-error">{detailItem.errorContent}</dd>
            </dl>
            <button
              type="button"
              className="invitation-errors__popup-close"
              onClick={() => setDetailItem(null)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* Solutions Section */}
      <div className="invitation-errors__solutions">
        <h2 className="invitation-errors__solutions-title">
          以下の手順をお試しください:
        </h2>
        <ol className="invitation-errors__solutions-list">
          <li>メールアドレスが正しいか確認してください。</li>
          <li>受信側のメールボックス容量や受信設定をご確認ください。</li>
          <li>メールの再送信をお試しください。</li>
          <li>エラーが続く場合はメールアドレス 設定をご確認ください。</li>
          <li>解決しない場合は、システム管理者へお問い合わせください。</li>
        </ol>
      </div>
    </div>
  );
};

export default InvitationErrors;
